import React, { useState, useEffect } from "react";
import "../../Sidebar/Pages/AccountSetting.css";
import { fetchAccountData } from "../../actioncreators/actioncreators";
import { useNavigate } from "react-router-dom"; // Add this import

const SubscriptionHistory = () => {
  const [activeTab, setActiveTab] = useState("current");
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const navigate = useNavigate();

  const fetchSubscriptionData = async (type) => {
    setIsLoading(true);
    try {
      const response = await fetchAccountData({ type: type });
      // Direct response mapping instead of response.organizationDetails
      setData(response);
    } catch (error) {
      console.error(`Error fetching ${type} subscription data:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Determine which type to fetch based on activeTab
    const typeMap = {
      current: "Current",
      previous: "Previous",
      upcoming: "Upcoming",
    };
    fetchSubscriptionData(typeMap[activeTab]);
  }, [activeTab]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setData(null); // Clear existing data when switching tabs
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (!data) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 text-center py-5">Loading...</div>
        </div>
      </div>
    );
  }

  // Add this function to handle view details
  const handleViewDetails = (subscription) => {
    setSelectedSubscription(subscription);
  };

  return (
    <div className="container-fluid">
      <div className="tabs-nav-row">
        <nav className="tabs-nav w-100">
          <button
            className={`tabs-nav-btn${
              activeTab === "current" ? " active" : ""
            }`}
            onClick={() => handleTabChange("current")}
            type="button"
          >
            <i className="bi bi-box-seam me-2"></i>
            Current Package
          </button>
          <button
            className={`tabs-nav-btn${
              activeTab === "previous" ? " active" : ""
            }`}
            onClick={() => handleTabChange("previous")}
            type="button"
          >
            <i className="bi bi-clock-history me-2"></i>
            Previous Packages
          </button>
          <button
            className={`tabs-nav-btn${
              activeTab === "upcoming" ? " active" : ""
            }`}
            onClick={() => handleTabChange("upcoming")}
            type="button"
          >
            <i className="bi bi-arrow-up-right-circle me-2"></i>
            Upcoming Packages
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="main-content1">
        {isLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Current Package Tab Content */}
            {activeTab === "current" && (
              <div>
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="card-title mb-0">Current Package</h5>
                      <p className="card-subtitle text-muted mb-0">
                        Details about your subscription package
                      </p>
                    </div>
                    <button
                      className="upgrade-cta-btn ms-3"
                      onClick={() => navigate("/home/subscription")}
                      type="button"
                    >
                      <span className="upgrade-cta-icon">
                        <i className="bi bi-arrow-up-right-circle-fill"></i>
                      </span>
                      <span className="upgrade-cta-text">Upgrade Plan</span>
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="package-header mb-4 d-flex align-items-center">
                      <div className="package-icon me-3">
                        <i
                          className="bi bi-box"
                          style={{
                            fontSize: "2rem",
                            color: "#0d6efd",
                          }}
                        ></i>
                      </div>
                      <div className="package-info flex-grow-1">
                        <h4 className="mb-1">{data?.package?.package_name}</h4>
                        <p className="mb-0 text-muted">
                          {data?.package?.package_description}
                        </p>
                        <span
                          className={`badge mt-1 ${
                            data?.subscriptionDetails?.subscription_status ===
                            "ACTIVE"
                              ? "bg-success"
                              : "bg-secondary"
                          } fw-semibold`}
                        >
                          {data?.subscriptionDetails?.subscription_status}
                        </span>
                      </div>
                      <div className="text-end">
                        <span
                          className={`badge ${
                            data?.package?.status === "ACTIVE"
                              ? "bg-success"
                              : "bg-secondary"
                          }`}
                        >
                          {data?.package?.package_type}
                        </span>
                        <div className="mt-2">
                          <strong>
                            ${data?.package?.monthly_price_per_user}
                          </strong>
                          /user/month
                        </div>
                      </div>
                    </div>
                    {/* Subscription Details in 4-column rows with thin horizontal lines */}
                    <div className="mb-4">
                      <h6
                        className="fw-bold mb-3"
                        style={{ fontSize: "1.05rem", color: "#333" }}
                      >
                        Subscription Summary
                      </h6>
                      <div
                        // style={{
                        //   backgroundColor: "#f9f9f9",
                        //   fontSize: "0.95rem",
                        //   borderRadius: "0.5rem",
                        //   border: "1px solid #eee",
                        //   overflow: "hidden"
                        // }}
                      >
                        {/* Row 1: Start, End, Renewal, Min Users */}
                        <div className="row m-0">
                          <div className="col-3 p-3">
                            <div className="text-muted small">Start Date</div>
                            <div className="fw-semibold">
                              {formatDate(
                                data?.subscriptionDetails?.subscription_start_date
                              )}
                            </div>
                          </div>
                          <div className="col-3 p-3">
                            <div className="text-muted small">End Date</div>
                            <div className="fw-semibold">
                              {formatDate(data?.subscriptionDetails?.subscription_end_date)}
                            </div>
                          </div>
                          <div className="col-3 p-3">
                            <div className="text-muted small">Daily Charge/User</div>
                            <div className="fw-semibold">
                              ${data?.subscriptionDetails?.daily_charge_per_user}
                            </div>
                          </div>
                          <div className="col-3 p-3">
                            <div className="text-muted small">Min Users</div>
                            <div className="fw-semibold">
                              {data?.package?.min_users}
                            </div>
                          </div>
                        </div>
                        {/* <hr style={{ margin: 0, borderTop: "1px solid #e0e0e0", height: "1px" }} /> */}
                        {/* Row 2: Min Days, Price/User, Max Users, Current Users */}
                        <div className="row m-0">
                          <div className="col-3 p-3">
                            <div className="text-muted small">
                              Minimum Subscription Days
                            </div>
                            <div className="fw-semibold">
                              {data?.package?.min_subscription_days}
                            </div>
                          </div>
                          <div className="col-3 p-3">
                            <div className="text-muted small">Price Per User</div>
                            <div className="fw-semibold">
                              {data?.package?.price_per_user
                                ? `$${data?.package?.price_per_user}`
                                : "Free"}
                            </div>
                          </div>
                          <div className="col-3 p-3">
                            <div className="text-muted small">Maximum Users</div>
                            <div className="fw-semibold">
                              {data?.subscriptionDetails?.max_users}
                            </div>
                          </div>
                          <div className="col-3 p-3">
                            <div className="text-muted small">Current Users</div>
                            <div className="fw-semibold">
                              {data?.orgDetails?.totalUsersCount} /{" "}
                              {data?.subscriptionDetails?.max_users}
                            </div>
                          </div>
                        </div>
                        <hr
                          style={{
                            margin: 0,
                            borderTop: "1px solid #e0e0e0",
                            height: "1px",
                          }}
                        />
                        {/* Row 3: Status (alone, but still in 4-col row for alignment) */}
                      </div>
                    </div>
                    {/* Features Table - compact, linear UI */}
                    <div className="mb-4">
                      <h6
                        className="fw-bold mb-3"
                        style={{ fontSize: "1.05rem", color: "#333" }}
                      >
                        Features
                      </h6>
                      <div className="table-responsive">
                        <table
                          className="table table-bordered table-sm"
                          style={{
                            fontSize: "0.95rem",
                            backgroundColor: "#fff",
                            border: "1px solid #dee2e6",
                          }}
                        >
                          <thead style={{ backgroundColor: "#f8f9fa" }}>
                            <tr>
                              <th
                                style={{
                                  width: "36%",
                                  fontWeight: "600",
                                  color: "#212529",
                                }}
                              >
                                Feature
                              </th>
                              <th className="d-none d-md-table-cell">
                                Description
                              </th>
                              <th
                                style={{
                                  width: "90px",
                                  textAlign: "center",
                                  fontWeight: "600",
                                  color: "#212529",
                                }}
                              >
                                Available In Current Plan
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {data?.features && data.features.length > 0 ? (
                              data.features.map((feature) => (
                                <tr key={feature.feature_id}>
                                  <td>{feature.feature_name}</td>
                                  <td className="d-none d-md-table-cell">
                                    {feature.feature_description}
                                  </td>
                                  <td className="text-center">
                                    <span className="text-success">✔</span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="3" className="text-center text-muted py-2">
                                  No features found for this package.
                                </td>
                              </tr>
                            )}

                            {/* Not Purchased Features */}
                            {data?.notPurchasedFeature &&
                              data.notPurchasedFeature.length > 0 &&
                              data.notPurchasedFeature.map((feature) => (
                                <tr key={feature.feature_id}>
                                  <td>{feature.feature_name}</td>
                                  <td className="d-none d-md-table-cell">
                                    {feature.feature_description}
                                  </td>
                                  <td className="text-center">
                                    <span className="text-danger">✘</span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Removed old Subscription Details card for cleaner UI */}
              </div>
            )}

            {/* Previous Packages Tab Content */}
            {activeTab === "previous" && (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="card-title mb-0">Previous Packages History</h5>
                    <p className="text-muted small mb-0">
                      View your past subscription details
                    </p>
                  </div>
                </div>
                <div className="card-body">
  {data?.subscriptionDetails?.length > 0 ? (
    <div className="table-responsive">
      <table className="table table-hover align-middle">
        <thead className="table-light" style={{ borderBottom: '1px solid #eaeaea' }} >
          <tr>
            <th style={{ width: "20%" }}>Start Date</th>
            <th style={{ width: "20%" }}>End Date</th>
            <th style={{ width: "20%" }}>Purchased Date</th>
            <th style={{ width: "20%" }}>Users</th>
            <th style={{ width: "20%" }}>Daily Rate</th>
            <th style={{ width: "20%" }}>Package Type</th>
            {/* <th style={{ width: "15%" }}>Details</th> */}
          </tr>
        </thead>
        <tbody>
          {data.subscriptionDetails.map((subscription) => (
            <tr key={subscription.subscription_id}>
              <td className="px-3 py-2">
                <span className="fw-semibold">
                  {formatDate(subscription.subscription_start_date)}
                </span>
              </td>
              <td className="px-3 py-2">
                <span className="fw-semibold">
                  {formatDate(subscription.subscription_end_date)}
                </span>
              </td>
              <td className="px-3 py-2">
        <span className="fw-semibold">
          {formatDate(subscription.creation_date_and_time)}
        </span>
      </td>
              <td className="px-3 py-2">
                <span className="fw-semibold">{subscription.max_users}</span>
                <span className="text-muted ms-1 small">(max users)</span>
              </td>
              <td className="px-3 py-2">
                <span className="fw-semibold">₹{subscription.daily_charge_per_user}</span>
                <span className="text-muted ms-1 small">(per user)</span>
              </td>
              <td className="px-3 py-2">
                <span className="small text-muted">{subscription.package_type}</span>
              </td>

              {/* <td className="py-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleViewDetails(subscription)}
                >
                  <i className="bi bi-eye me-1"></i>
                  View
                </button>
              </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="text-center py-4">
      <i
        className="bi bi-clock-history text-muted"
        style={{ fontSize: "2rem" }}
      ></i>
      <p className="text-muted mt-2 mb-0">
        No previous package history available.
      </p>
    </div>
  )}
</div>

              </div>
            )}

            {/* Upcoming Packages Tab Content */}
            {activeTab === "upcoming" && (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="card-title mb-0">Upcoming Package Changes</h5>
                    <p className="text-muted small mb-0">View your scheduled subscription changes</p>
                  </div>
                </div>
                <div className="card-body">
  {data?.subscriptionDetails?.length > 0 ? (
    <div className="table-responsive">
      <table className="table table-hover align-middle">
        <thead className="table-light" style={{ borderBottom: '1px solid #f0f0f0' }}>

          <tr>
            <th style={{ width: "20%" }}>Start Date</th>
            <th style={{ width: "20%" }}>End Date</th>
            <th style={{ width: "20%" }}>Purchased Date</th>
            <th style={{ width: "20%" }}>Users</th>
            <th style={{ width: "20%" }}>Daily Rate</th>
            <th style={{ width: "20%" }}>Package Type</th>
            {/* <th style={{ width: "15%" }}>Details</th> */}
          </tr>
        </thead>
        <tbody>
          {data.subscriptionDetails.map((subscription) => (
            <tr key={subscription.subscription_id}>
              <td className="px-3 py-2">
                <span className="fw-semibold">
                  {formatDate(subscription.subscription_start_date)}
                </span>
              </td>
              <td className="px-3 py-2">
                <span className="fw-semibold">
                  {formatDate(subscription.subscription_end_date)}
                </span>
              </td>
              <td className="px-3 py-2">
        <span className="fw-semibold">
          {formatDate(subscription.creation_date_and_time)}
        </span>
      </td>
              <td className="px-3 py-2">
                <span className="fw-semibold">{subscription.max_users}</span>
                <span className="text-muted ms-1 small">(max users)</span>
              </td>
              <td className="px-3 py-2">
                <span className="fw-semibold">₹{subscription.daily_charge_per_user}</span>
                <span className="text-muted ms-1 small">(per user)</span>
              </td>
              <td className="px-3 py-2">
                <span className="small text-muted">{subscription.package_type}</span>
              </td>
              {/* <td className="py-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleViewDetails(subscription)}
                >
                  <i className="bi bi-eye me-1"></i>
                  View
                </button>
              </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="text-center py-4">
      <i className="bi bi-calendar-plus text-muted" style={{ fontSize: "2rem" }}></i>
      <p className="text-muted mt-2 mb-0">
        No upcoming package changes scheduled.
      </p>
    </div>
  )}
</div>

              </div>
            )}
          </>
        )}
      </div>

      {/* Subscription Details Modal */}
      {selectedSubscription && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Subscription Details</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedSubscription(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-6">
                    <label className="text-muted small">Subscription ID</label>
                    <div className="fw-semibold">{selectedSubscription.subscription_id}</div>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small">Package ID</label>
                    <div className="fw-semibold">{selectedSubscription.package_id}</div>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small">Start Date</label>
                    <div className="fw-semibold">
                      {formatDate(selectedSubscription.subscription_start_date)}
                    </div>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small">End Date</label>
                    <div className="fw-semibold">
                      {formatDate(selectedSubscription.subscription_end_date)}
                    </div>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small">Daily Charge</label>
                    <div className="fw-semibold">${selectedSubscription.daily_charge_per_user}/user</div>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small">Max Users</label>
                    <div className="fw-semibold">{selectedSubscription.max_users}</div>
                  </div>
                  {/* <div className="col-12">
                    <label className="text-muted small">Status</label>
                    <div>
                      <span className={`badge ${
                        selectedSubscription.subscription_status === "ACTIVE" 
                          ? "bg-success" 
                          : "bg-secondary"
                      }`}>
                        {selectedSubscription.subscription_status}
                      </span>
                    </div> */}
                  {/* </div> */}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedSubscription(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionHistory;